import React, { useEffect, useState } from 'react'
import { Button, Card, Space, message, theme, Tooltip, Typography, Spin, Skeleton, Table, Row, Col } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh, faShoppingCart, faPlus } from '@fortawesome/free-solid-svg-icons'
import BillingTool from '../../../../Bookings/components/Examination Components/BillingTool'
import BillingToolWrapper from './BillingToolWrapper'
import { useSelector } from 'react-redux'
import ObjectID from 'bson-objectid'

const { Title } = Typography

/**
 * Component for managing invoice items
 */
const InvoiceItems = ({
  items,
  setItems,
  bookingRef,
  patientRecord,
  loading,
  onRefresh,
  billingWebSocket,
  selectedInvoiceType
}) => {
  const { token } = theme.useToken()
  const authState = useSelector((state) => state.auth)
  const [mockWebSocket, setMockWebSocket] = useState(null)

  // Create a mock WebSocket if one isn't provided
  useEffect(() => {
    if (!billingWebSocket || !billingWebSocket.socket) {
      setMockWebSocket({
        socket: {
          send: (message) => {
            // Force a refresh when "sent" to simulate WebSocket behavior
            setTimeout(onRefresh, 500)
          }
        },
        connected: true
      })
    }
  }, [billingWebSocket, onRefresh])

  // Initialize with an empty row if no items
  useEffect(() => {
    if (items.length === 0 && !loading) {
      setItems([
        {
          type: undefined,
          code: undefined,
          name: undefined,
          qty: 0,
          icd10: undefined,
          medicalAid: '0',
          patient: '0',
          total: '0',
          _id: new ObjectID().toString()
        }
      ])
    }
  }, [items, setItems, loading])

  // Use the actual WebSocket if available, otherwise use the mock
  const effectiveWebSocket = billingWebSocket?.socket ? billingWebSocket : mockWebSocket

  // Enhanced item section style
  const tableWrapperStyle = {
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden'
  }

  // Custom CSS for the BillingTool table
  const tableStyles = `
    .invoice-item-table .ant-table-thead > tr > th {
      background-color: rgba(24, 144, 255, 0.1);
      color: rgba(0, 0, 0, 0.85);
      font-weight: 500;
    }
    
    .invoice-item-table .ant-table-tbody > tr:hover > td {
      background-color: rgba(24, 144, 255, 0.05);
    }
    
    .invoice-item-table .ant-table {
      border-radius: 8px;
      overflow: hidden;
    }
    
    .invoice-item-table .ant-table-container {
      border-radius: 8px;
      overflow: hidden;
    }
    
    .invoice-item-table .ant-input-number,
    .invoice-item-table .ant-input,
    .invoice-item-table .ant-select-selector {
      border-radius: 4px;
    }
    
    .invoice-item-table .ant-table-row {
      transition: all 0.3s ease;
    }
  `

  const handleAddItem = () => {
    const newItemObject = {
      type: undefined,
      code: undefined,
      name: undefined,
      qty: 0,
      icd10: undefined,
      medicalAid: '0',
      patient: '0',
      total: '0',
      _id: new ObjectID().toString()
    }
    setItems([...items, newItemObject])
  }

  return (
    <>
      <style>{tableStyles}</style>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faShoppingCart} style={{ color: token.colorPrimary, marginRight: '8px' }} />
            <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>Invoice Items</span>
          </div>
          <div>
            <Button
              icon={<FontAwesomeIcon icon={faRefresh} />}
              onClick={onRefresh}
              style={{ marginRight: '8px' }}
              disabled={loading}
            />
            <Button icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleAddItem} disabled={loading} />
          </div>
        </div>
      </div>

      <div style={tableWrapperStyle} className='invoice-item-table'>
        {effectiveWebSocket ? (
          <BillingToolWrapper
            loading={loading}
            isInvoiceForm={true}
            itemSelection={items}
            setItemSelection={setItems}
            bookingData={{
              _id: bookingRef,
              patientRecord
            }}
            isMacroForm={false}
            billingRecordsLoading={false}
            selectedInvoiceType={selectedInvoiceType}
            billingWebSocket={effectiveWebSocket}
            hideAdd={false}
            readOnly={false}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>Loading billing tool...</div>
        )}
      </div>
    </>
  )
}

export default InvoiceItems
